import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Snackbar,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ReplayIcon from "@mui/icons-material/Replay";
import CloseIcon from "@mui/icons-material/Close";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import TrainIcon from "@mui/icons-material/Train";
import {
  BANNER_RANGES,
  BannerCardsStyle,
  BannerMode,
  DEFAULT_BANNER_CARDS_STYLE,
  ENGINE_PRESETS,
  EnginePreset,
  PreviewBanner,
  SAMPLE_BANNERS,
  TITLE_POSITIONS,
  getBannerCardsStyle,
  getOrgBannersForPreview,
  saveBannerCardsStyle,
} from "../../../../service/BuyerConfiguration/BuyerAppDashboard/bannerCardsService";
import { isHttpUrl } from "../../../../service/BuyerConfiguration/BuyerAppDashboard/homeThemesService";
import { GREEN, GREEN_DARK, deepEqual, errMsg } from "../ThemeStudio/themeUtils";
import { AssetUrlField, ColorField, SliderRow, times } from "../ThemeStudio/fields";
import { AssetLibrary } from "../ThemeStudio/AssetLibrary";
import { Section, btnGreen, btnOutline, switchSx } from "../CardStudio/CardLookTab";
import { BannerCardsPreview } from "./BannerCardsPreview";
import { BoxShapeDialog } from "./BoxShapeDialog";
import { BoxArt, EngineThumb } from "./bannerArt";

const MODES: Array<{ key: BannerMode; label: string; hint: string; icon: React.ReactNode }> = [
  { key: "row", label: "Row", hint: "Three boxes side by side", icon: <ViewColumnIcon /> },
  { key: "carousel", label: "Carousel", hint: "Boxes slide one by one", icon: <ViewCarouselIcon /> },
  { key: "train", label: "Train", hint: "An engine pulls a coach per banner", icon: <TrainIcon /> },
];

const ENGINE_LABELS: Record<EnginePreset, string> = {
  steam: "Steam",
  bullet: "Bullet",
  toy: "Toy",
  festival: "Festival",
};

const POSITION_LABELS: Record<string, string> = { top: "Top", center: "Centre", bottom: "Bottom" };

const toggleSx = {
  "& .Mui-selected": { bgcolor: "rgba(0,177,79,0.12) !important", color: `${GREEN_DARK} !important` },
  "& .MuiToggleButton-root": { textTransform: "none", px: 2 },
} as const;

const SwitchRow: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void; hint?: string }> = ({
  label,
  checked,
  onChange,
  hint,
}) => (
  <Box>
    <FormControlLabel
      control={<Switch checked={checked} onChange={(e) => onChange(e.target.checked)} sx={switchSx} />}
      label={<Typography sx={{ fontSize: 13.5 }}>{label}</Typography>}
    />
    {hint && <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: -0.5, ml: 6 }}>{hint}</Typography>}
  </Box>
);

const Pick: React.FC<{
  selected: boolean;
  onClick: () => void;
  label: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ selected, onClick, label, hint, children }) => (
  <Box
    role="button"
    tabIndex={0}
    aria-pressed={selected}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    }}
    sx={{
      p: 1.25,
      borderRadius: 2,
      cursor: "pointer",
      border: `2px solid ${selected ? GREEN : "rgba(0,0,0,0.1)"}`,
      bgcolor: selected ? "rgba(0,177,79,0.06)" : "transparent",
      textAlign: "center",
      minWidth: 104,
      flex: "1 1 104px",
      "&:hover": { borderColor: selected ? GREEN : "rgba(0,0,0,0.3)" },
      "&:focus-visible": { outline: `2px solid ${GREEN_DARK}`, outlineOffset: 2 },
    }}
  >
    <Box sx={{ color: selected ? GREEN_DARK : "text.secondary", display: "flex", justifyContent: "center" }}>
      {children}
    </Box>
    <Typography sx={{ fontSize: 13, fontWeight: 700, mt: 0.5 }}>{label}</Typography>
    {hint && <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{hint}</Typography>}
  </Box>
);

/** Engine art: link field plus the theme asset library (images and Lottie). */
const EngineArtField: React.FC<{ value: string | null; onChange: (v: string | null) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = React.useState(false);
  const bad = !!value && !isHttpUrl(value);
  return (
    <>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          size="small"
          label="Own engine art"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value.trim() ? e.target.value : null)}
          placeholder="https://…"
          fullWidth
          error={bad}
          helperText={
            bad
              ? "Must be an http(s) link, or it is dropped on save"
              : "An image or Lottie (.json) facing left. Replaces the preset engine."
          }
          InputProps={{
            endAdornment: value ? (
              <Tooltip title="Clear">
                <IconButton size="small" onClick={() => onChange(null)} aria-label="Clear own engine art">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : undefined,
          }}
        />
        <Button
          variant="outlined"
          startIcon={<PhotoLibraryOutlinedIcon />}
          onClick={() => setOpen(true)}
          sx={{ textTransform: "none", borderColor: GREEN, color: GREEN, height: 40, whiteSpace: "nowrap", flexShrink: 0 }}
        >
          Library
        </Button>
      </Stack>
      {open && (
        <AssetLibrary
          open
          kind="image"
          onClose={() => setOpen(false)}
          onSelect={(url) => {
            onChange(url);
            setOpen(false);
          }}
        />
      )}
    </>
  );
};

/** Banner Cards Studio: look and motion of the banner boxes under the search bar. */
export const BannerCardsStudio: React.FC = () => {
  const navigate = useNavigate();
  const [saved, setSaved] = React.useState<BannerCardsStyle | null>(null);
  const [defaults, setDefaults] = React.useState<BannerCardsStyle>(DEFAULT_BANNER_CARDS_STYLE);
  const [draft, setDraft] = React.useState<BannerCardsStyle>(DEFAULT_BANNER_CARDS_STYLE);
  const [banners, setBanners] = React.useState<PreviewBanner[]>(SAMPLE_BANNERS);
  const [usingSamples, setUsingSamples] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [toast, setToast] = React.useState<{ msg: string; ok: boolean } | null>(null);
  const [pendingNav, setPendingNav] = React.useState<null | (() => void)>(null);
  const [shapeOpen, setShapeOpen] = React.useState(false);
  const [restartKey, setRestartKey] = React.useState(0);

  const load = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await getBannerCardsStyle();
      setSaved(r.style);
      setDraft(r.style);
      setDefaults(r.defaults);
    } catch (e) {
      setLoadError(errMsg(e, "Could not load the banner style. Check your connection and try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
    // The preview uses the real organisation banners when there are any.
    getOrgBannersForPreview()
      .then((list) => {
        if (list.length) {
          setBanners(list.slice(0, 3));
          setUsingSamples(false);
        }
      })
      .catch(() => undefined);
  }, [load]);

  const dirty = !!saved && !deepEqual(draft, saved);

  // Closing or reloading the tab with unsaved changes: let the browser ask.
  React.useEffect(() => {
    if (!dirty) return;
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [dirty]);

  const guard = (fn: () => void) => (dirty ? setPendingNav(() => fn) : fn());

  const set = <K extends keyof BannerCardsStyle>(k: K, v: BannerCardsStyle[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const setBox = (patch: Partial<BannerCardsStyle["box"]>) => setDraft((d) => ({ ...d, box: { ...d.box, ...patch } }));
  const setTitle = (patch: Partial<BannerCardsStyle["title"]>) =>
    setDraft((d) => ({ ...d, title: { ...d.title, ...patch } }));
  const setTrain = (patch: Partial<BannerCardsStyle["train"]>) =>
    setDraft((d) => ({ ...d, train: { ...d.train, ...patch } }));
  const setEngine = (patch: Partial<BannerCardsStyle["train"]["engine"]>) =>
    setDraft((d) => ({ ...d, train: { ...d.train, engine: { ...d.train.engine, ...patch } } }));
  const setCoach = (patch: Partial<BannerCardsStyle["train"]["coach"]>) =>
    setDraft((d) => ({ ...d, train: { ...d.train, coach: { ...d.train.coach, ...patch } } }));

  const save = async () => {
    setSaving(true);
    try {
      const s = await saveBannerCardsStyle(draft);
      setSaved(s);
      setDraft(s);
      setToast({ msg: "Saved. Apps update in a few seconds.", ok: true });
    } catch (e) {
      setToast({ msg: errMsg(e, "Could not save the banner style. Try again."), ok: false });
    } finally {
      setSaving(false);
    }
  };

  const tr = draft.train;

  return (
    <Box sx={{ p: 3, maxWidth: 1280 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => guard(() => navigate("/buyer-app-dashboard"))}
        sx={{ textTransform: "none", color: GREEN_DARK, mb: 1, ml: -1 }}
      >
        Back to Buyer App Dashboard
      </Button>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 24, mb: 0.5 }}>Banner Cards Studio</Typography>
        <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
          The three boxes under the search bar on the buyer home. Photos, videos and titles come from Banner
          Management; here you set their shape, style and how they move.
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ py: 10, textAlign: "center" }}>
          <CircularProgress sx={{ color: GREEN }} />
        </Box>
      ) : loadError || !saved ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={load} sx={{ textTransform: "none" }}>
              Retry
            </Button>
          }
        >
          {loadError ?? "Could not load the banner style."}
        </Alert>
      ) : (
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">
          {/* Preview */}
          <Box sx={{ position: { md: "sticky" }, top: { md: 16 }, flexShrink: 0, width: { xs: "100%", md: 392 } }}>
            <Box sx={{ bgcolor: "#F4F5F7", borderRadius: 4, p: 2, maxWidth: 392, boxSizing: "border-box" }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                  {usingSamples ? "Live preview with sample banners" : "Live preview with your organisation banners"}
                </Typography>
                {draft.mode === "train" && (
                  <Tooltip title="Run the train again">
                    <IconButton size="small" aria-label="Run the train again" onClick={() => setRestartKey((k) => k + 1)}>
                      <ReplayIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
              <BannerCardsPreview style={draft} banners={banners} restartKey={restartKey} />
              <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1.5 }}>
                A theme can set its own banner style (Theme Studio). While such a theme is live, the app uses the
                theme's style.
              </Typography>
              {usingSamples && (
                <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1 }}>
                  No organisation banners yet. Add them in Banner Management to see your own photos here.
                </Typography>
              )}
            </Box>
          </Box>

          {/* Controls */}
          <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button variant="contained" onClick={save} disabled={saving || !dirty} sx={btnGreen}>
                {saving ? "Saving…" : "Save"}
              </Button>
              <Button variant="outlined" onClick={() => setDraft(saved)} disabled={saving || !dirty} sx={btnOutline}>
                Discard changes
              </Button>
              <Button
                startIcon={<RestartAltIcon />}
                onClick={() => {
                  setDraft(defaults);
                  setToast({ msg: "Reset to the default style. Save to apply it.", ok: true });
                }}
                disabled={saving || deepEqual(draft, defaults)}
                sx={{ textTransform: "none", color: GREEN_DARK }}
              >
                Reset to default
              </Button>
              {dirty && <Typography sx={{ fontSize: 12.5, color: "#B26A00" }}>Unsaved changes</Typography>}
            </Stack>

            <Section title="Mode" hint="How the boxes sit under the search bar.">
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {MODES.map((m) => (
                  <Pick key={m.key} selected={draft.mode === m.key} onClick={() => set("mode", m.key)} label={m.label} hint={m.hint}>
                    {m.icon}
                  </Pick>
                ))}
              </Stack>
            </Section>

            <Section title="Box" hint="Shape and frame of every banner box. In the train, each coach carries one box.">
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <Box sx={{ bgcolor: "#00B14F", borderRadius: 2, p: 1, lineHeight: 0 }}>
                  <svg width={Math.round(48 * draft.box.widthRatio)} height={48} aria-hidden style={{ overflow: "visible" }}>
                    <BoxArt
                      banner={{ ...banners[0], title: "" }}
                      style={draft}
                      w={Math.round(48 * draft.box.widthRatio)}
                      h={48}
                    />
                  </svg>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    {draft.box.shape ? `Custom shape · ${draft.box.shape.points.length} points` : "Rounded rectangle"}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Button variant="outlined" size="small" onClick={() => setShapeOpen(true)} sx={btnOutline}>
                      Edit shape
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setBox({ shape: null })}
                      disabled={!draft.box.shape}
                      sx={{ textTransform: "none", color: GREEN_DARK }}
                    >
                      Use rounded rectangle
                    </Button>
                  </Stack>
                </Box>
              </Stack>
              <SliderRow
                label="Corner radius"
                {...BANNER_RANGES.radius}
                value={draft.box.radius}
                onChange={(v) => setBox({ radius: v })}
                format={(v) => `${v} px`}
                hint={draft.box.shape ? "Used only with the rounded rectangle. Custom shapes use their own roundness." : undefined}
              />
              <SliderRow label="Height" {...BANNER_RANGES.height} value={draft.box.height} onChange={(v) => setBox({ height: v })} format={(v) => `${v} px`} />
              <SliderRow
                label="Width"
                {...BANNER_RANGES.widthRatio}
                value={draft.box.widthRatio}
                onChange={(v) => setBox({ widthRatio: v })}
                format={(v) => `${times(v)} the height`}
                hint="Carousel and train. In row mode the boxes share the full width."
              />
              <SliderRow label="Gap between boxes" {...BANNER_RANGES.gap} value={draft.box.gap} onChange={(v) => setBox({ gap: v })} format={(v) => `${v} px`} />
              <ColorField label="Border colour" value={draft.box.borderColor} onChange={(v) => setBox({ borderColor: v })} />
              <SliderRow
                label="Border width"
                {...BANNER_RANGES.borderWidth}
                value={draft.box.borderWidth}
                onChange={(v) => setBox({ borderWidth: v })}
                format={(v) => (v ? `${v} px` : "No border")}
              />
              <SwitchRow label="Drop shadow" checked={draft.box.shadow} onChange={(v) => setBox({ shadow: v })} />
            </Section>

            <Section title="Title" hint="The banner title from Banner Management, drawn on the photo.">
              <SwitchRow label="Show title" checked={draft.title.show} onChange={(v) => setTitle({ show: v })} />
              {draft.title.show && (
                <>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>Position</Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={draft.title.position}
                      onChange={(_, v) => v && setTitle({ position: v })}
                      sx={toggleSx}
                    >
                      {TITLE_POSITIONS.map((p) => (
                        <ToggleButton key={p} value={p}>
                          {POSITION_LABELS[p]}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>
                  <SliderRow label="Text size" {...BANNER_RANGES.titleSize} value={draft.title.size} onChange={(v) => setTitle({ size: v })} format={(v) => `${v} px`} />
                  <ColorField label="Text colour" value={draft.title.color} onChange={(v) => setTitle({ color: v })} />
                  <SwitchRow
                    label="Dark fade behind the title"
                    checked={draft.title.fade}
                    onChange={(v) => setTitle({ fade: v })}
                    hint="Keeps light text readable on bright photos."
                  />
                </>
              )}
            </Section>

            {draft.mode === "carousel" && (
              <Section title="Carousel" hint="The boxes slide along one at a time.">
                <SliderRow
                  label="Slide every"
                  {...BANNER_RANGES.intervalSec}
                  value={draft.carousel.intervalSec}
                  onChange={(v) => set("carousel", { intervalSec: v })}
                  format={(v) => `${v}s`}
                />
              </Section>
            )}

            {draft.mode === "train" && (
              <>
                <Section title="Engine" hint="Pick a ready-made engine, or use your own art.">
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {ENGINE_PRESETS.map((p) => (
                      <Pick
                        key={p}
                        selected={tr.engine.preset === p && !tr.engine.artUrl}
                        onClick={() => setEngine({ preset: p, artUrl: null })}
                        label={ENGINE_LABELS[p]}
                      >
                        <EngineThumb preset={p} color={tr.engine.color} accent={tr.engine.accent} wheelColor={tr.wheels.color} />
                      </Pick>
                    ))}
                  </Stack>
                  <EngineArtField value={tr.engine.artUrl} onChange={(v) => setEngine({ artUrl: v })} />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                    <ColorField label="Engine colour" value={tr.engine.color} onChange={(v) => setEngine({ color: v })} />
                    <ColorField label="Engine accent" value={tr.engine.accent} onChange={(v) => setEngine({ accent: v })} />
                  </Box>
                </Section>

                <Section title="Coaches" hint="One coach per banner, with the banner photo in a frame.">
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                    <ColorField label="Coach colour" value={tr.coach.color} onChange={(v) => setCoach({ color: v })} />
                    <ColorField label="Coach colour 2 (fades to)" value={tr.coach.color2} onChange={(v) => setCoach({ color2: v })} />
                    <ColorField label="Stripe" value={tr.coach.stripe} onChange={(v) => setCoach({ stripe: v })} />
                    <ColorField label="Photo frame" value={tr.coach.frame} onChange={(v) => setCoach({ frame: v })} />
                  </Box>
                  <SwitchRow label="Show wheels" checked={tr.wheels.show} onChange={(v) => setTrain({ wheels: { ...tr.wheels, show: v } })} />
                  {tr.wheels.show && (
                    <ColorField label="Wheel colour" value={tr.wheels.color} onChange={(v) => setTrain({ wheels: { ...tr.wheels, color: v } })} />
                  )}
                </Section>

                <Section title="Motion" hint="How the train runs across the green top of the home screen.">
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>Run</Typography>
                    <ToggleButtonGroup exclusive size="small" value={tr.motion} onChange={(_, v) => v && setTrain({ motion: v })} sx={toggleSx}>
                      <ToggleButton value="stop">Stop at the station</ToggleButton>
                      <ToggleButton value="continuous">Keep running</ToggleButton>
                    </ToggleButtonGroup>
                    <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.75 }}>
                      {tr.motion === "stop"
                        ? "The train pulls in, stops with every coach on screen, then leaves."
                        : "The train crosses the screen without stopping, then comes round again."}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>Direction</Typography>
                    <ToggleButtonGroup exclusive size="small" value={tr.direction} onChange={(_, v) => v && setTrain({ direction: v })} sx={toggleSx}>
                      <ToggleButton value="left">Right to left</ToggleButton>
                      <ToggleButton value="right">Left to right</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <SliderRow label="Speed" {...BANNER_RANGES.speed} value={tr.speed} onChange={(v) => setTrain({ speed: v })} format={(v) => `${v} px/s`} />
                  {tr.motion === "stop" && (
                    <SliderRow label="Stop for" {...BANNER_RANGES.stopSec} value={tr.stopSec} onChange={(v) => setTrain({ stopSec: v })} format={(v) => `${v}s`} />
                  )}
                  <SliderRow
                    label="Wait between trips"
                    {...BANNER_RANGES.gapSec}
                    value={tr.gapSec}
                    onChange={(v) => setTrain({ gapSec: v })}
                    format={(v) => (v ? `${v}s` : "No wait")}
                  />
                  <SwitchRow
                    label="Smoke from the chimney"
                    checked={tr.smoke}
                    onChange={(v) => setTrain({ smoke: v })}
                    hint={tr.engine.preset === "bullet" && !tr.engine.artUrl ? "The bullet engine has no chimney." : undefined}
                  />
                  <SwitchRow label="Gentle bounce while moving" checked={tr.bounce} onChange={(v) => setTrain({ bounce: v })} />
                </Section>

                <Section title="Track, sound and touch">
                  <SwitchRow label="Show track" checked={tr.track.show} onChange={(v) => setTrain({ track: { ...tr.track, show: v } })} />
                  {tr.track.show && (
                    <ColorField label="Track colour" value={tr.track.color} onChange={(v) => setTrain({ track: { ...tr.track, color: v } })} />
                  )}
                  <AssetUrlField
                    label="Train sound"
                    kind="audio"
                    value={tr.soundUrl}
                    onChange={(v) => setTrain({ soundUrl: v })}
                    hint="Optional. Plays in the app with the train; the preview here is silent."
                  />
                  <SwitchRow label="Haptic tap when the train arrives" checked={tr.haptic} onChange={(v) => setTrain({ haptic: v })} />
                </Section>
              </>
            )}

            {draft.mode !== "train" && (
              <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                Switch the mode to {draft.mode === "row" ? "Carousel or Train" : "Train"} to see its settings. Saved
                settings for other modes are kept.
              </Typography>
            )}
          </Box>
        </Stack>
      )}

      <BoxShapeDialog
        open={shapeOpen}
        style={draft}
        banners={banners}
        onClose={() => setShapeOpen(false)}
        onApply={(shape) => {
          setBox({ shape });
          setShapeOpen(false);
        }}
      />

      <Dialog open={!!pendingNav} onClose={() => setPendingNav(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Discard changes?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>You have unsaved changes to the banner style. They will be lost.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingNav(null)} sx={{ textTransform: "none", color: GREEN_DARK }}>
            Keep editing
          </Button>
          <Button
            color="error"
            onClick={() => {
              const fn = pendingNav;
              setPendingNav(null);
              if (saved) setDraft(saved);
              fn?.();
            }}
            sx={{ textTransform: "none" }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.ok ? "success" : "error"} onClose={() => setToast(null)} sx={{ width: "100%" }}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default BannerCardsStudio;
